import { useQuery } from '@tanstack/react-query';
import type {
  FilterOptionsDto,
  VoterDto,
  VoterListResponse,
  VoterMapPointDto,
  VoterMapFilter,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { queryKeys, VoterListParams } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch paginated voter list with filters
 */
export function useVoters(params: VoterListParams = {}) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.voters.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle array params like tagIds
            value.forEach((v) => searchParams.append(key, String(v)));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });

      return apiJson<VoterListResponse>(`/api/voters?${searchParams}`, { error: 'Failed to fetch voters' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/**
 * Fetch a single voter by ID
 */
export function useVoter(voterId: string | undefined) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.voters.detail(voterId ?? ''),
    queryFn: () => apiJson<VoterDto>(`/api/voters/${voterId}`, { error: 'Failed to fetch voter' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!voterId,
  });
}

// ============================================================================
// Map Point Hooks
// ============================================================================

/**
 * Fetch all voter coordinates for map rendering (no pagination)
 */
export function useVoterMapPoints(enabled = true, filters?: VoterMapFilter) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.voters.mapPoints(filters),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (filters) {
        if (filters.contactStatus) searchParams.append('contactStatus', filters.contactStatus);
        if (filters.zipCodes?.length) searchParams.append('zipCodes', filters.zipCodes.join(','));
        if (filters.party?.length) searchParams.append('party', filters.party.join(','));
        if (filters.voteFrequencyMin != null) searchParams.append('voteFrequencyMin', String(filters.voteFrequencyMin));
        if (filters.voteFrequencyMax != null) searchParams.append('voteFrequencyMax', String(filters.voteFrequencyMax));
        if (filters.support?.length) searchParams.append('support', filters.support.join(','));
        if (filters.education?.length) searchParams.append('education', filters.education.join(','));
        if (filters.conservativeDonor != null) searchParams.append('conservativeDonor', String(filters.conservativeDonor));
        if (filters.liberalDonor != null) searchParams.append('liberalDonor', String(filters.liberalDonor));
        if (filters.tagIds?.length) searchParams.append('tagIds', filters.tagIds.join(','));
      }

      const qs = searchParams.toString();
      return apiJson<VoterMapPointDto[]>(`/api/voters/map-points${qs ? `?${qs}` : ''}`, {
        error: 'Failed to fetch voter map points',
      });
    },
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Filter options for voter map filter dropdowns (distinct party/education values)
 */
export function useVoterFilterOptions() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.voters.filterOptions(),
    queryFn: async () => {
      return apiJson<FilterOptionsDto>(`/api/voters/filter-options`, { error: 'Failed to fetch filter options' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}
